import { parsePortalDesign, type PortalDesign } from "@axl/membership-sdk";
import { useMemo } from "react";

import { useSchool } from "../hooks/useSchool";

/**
 * The school's portal design (`school.designJson`, admin → membership portal → design) with every default
 * filled in by the SDK. Defaults reproduce the portal as it was before the settings existed.
 *
 * Testing aids, never shown to students:
 * - `?nav=top|side` and `?lesson=tabs|feed` override the desktop navigation / step layout for this tab
 *   (kept in sessionStorage so they survive navigation; `?nav=` / `?lesson=` with no value clears).
 * - DEV builds only: `?designJson=<json>` (or localStorage `school.design-override`) replaces the school's
 *   `designJson` — try settings before the backend has them; `?designJson=` with no value clears it.
 */
const OVERRIDE_KEY = "school.design-override";
const SESSION_KEY = "school.design-query";

type QueryOverrides = { nav?: "top" | "side"; lesson?: "tabs" | "feed" };

const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

function readQueryOverrides(): QueryOverrides {
  if (typeof window === "undefined") return {};
  const stored = safe(() => JSON.parse(window.sessionStorage.getItem(SESSION_KEY) ?? "{}") as QueryOverrides, {});
  const q = new URLSearchParams(window.location.search);
  const next: QueryOverrides = { ...stored };
  if (q.has("nav")) {
    const nav = q.get("nav");
    if (nav === "top" || nav === "side") next.nav = nav;
    else delete next.nav;
  }
  if (q.has("lesson")) {
    const lesson = q.get("lesson");
    if (lesson === "tabs" || lesson === "feed") next.lesson = lesson;
    else delete next.lesson;
  }
  safe(() => window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(next)), undefined);
  return next;
}

function readDevOverride(): string | null {
  if (!import.meta.env.DEV || typeof window === "undefined") return null;
  const q = new URLSearchParams(window.location.search);
  if (q.has("designJson")) {
    const value = q.get("designJson") ?? "";
    safe(() => (value ? window.localStorage.setItem(OVERRIDE_KEY, value) : window.localStorage.removeItem(OVERRIDE_KEY)), undefined);
    return value || null;
  }
  return safe(() => window.localStorage.getItem(OVERRIDE_KEY), null);
}

// read once per page load: the overrides are for a test session, not something that changes live
const queryOverrides = readQueryOverrides();
const devOverride = readDevOverride();

/** Pure: the design the portal applies for a given `designJson`, with the testing overrides on top. */
export function resolvePortalDesign(designJson: string | null | undefined): PortalDesign {
  const design = parsePortalDesign(devOverride ?? designJson);
  if (!queryOverrides.nav && !queryOverrides.lesson) return design;
  return {
    ...design,
    header: { ...design.header, nav: queryOverrides.nav ?? design.header.nav },
    lesson: { ...design.lesson, layout: queryOverrides.lesson ?? design.lesson.layout },
  };
}

export const usePortalDesign = (): PortalDesign => {
  const school = useSchool();
  return useMemo(() => resolvePortalDesign(school.designJson), [school.designJson]);
};
